import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkPlus, Bookmark, Trash2, ChevronDown, Check } from "lucide-react";

/**
 * Saved-profile picker for Agency / Physician.
 *
 * Props:
 *   label       — e.g. "Saved agencies"
 *   profiles    — array from listAgencies() / listPhysicians()
 *   onApply(p)  — merge that profile into the wizard section
 *   onSave()    — capture current wizard values into the registry
 *   onDelete(id) — remove a profile
 *   emptyHint   — string shown when no profiles exist
 *   testid
 */
export default function SavedProfilePicker({
  label,
  profiles,
  onApply,
  onSave,
  onDelete,
  emptyHint,
  testid,
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = (e) => { if (!e.target.closest(`[data-picker='${testid}']`)) setOpen(false); };
    if (open) document.addEventListener("click", h);
    return () => document.removeEventListener("click", h);
  }, [open, testid]);

  const onSaveClick = () => {
    const res = onSave?.();
    if (!res) return;
    if (res.ok) {
      toast.success(res.updated ? "Profile updated." : "Profile saved.");
    } else {
      toast.error(res.error || "Could not save profile.");
    }
  };

  return (
    <div
      className="rounded-xl border border-[#B7CADA] bg-[#f4f6f1] p-3 flex flex-wrap items-center gap-2"
      data-picker={testid}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 min-w-0 mr-auto">
        <Bookmark className="w-4 h-4 text-[#0A3D79]" />
        <div className="text-[12.5px] font-semibold text-[#0B1E3A] truncate">{label}</div>
      </div>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((x) => !x)}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-white border border-[#B7CADA] text-[12.5px] font-semibold text-[#0B1E3A] hover:bg-[#E7F1FB]"
          data-testid={`${testid}-open`}
        >
          {profiles?.length ? `Pick from ${profiles.length}` : "No saved profiles"}
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        {open && (
          <div
            className="absolute right-0 top-[110%] z-40 w-[340px] max-h-[360px] overflow-y-auto rounded-xl bg-white shadow-[0_12px_36px_rgba(26,33,28,0.18)] border border-[#D6E3EF] py-1"
            data-testid={`${testid}-menu`}
          >
            {!profiles?.length && (
              <div className="px-4 py-4 text-[12.5px] text-[#3A5275]">
                {emptyHint || "No saved profiles yet. Fill in the form then tap Save to reuse it on the next client."}
              </div>
            )}
            {profiles.map((p) => (
              <div
                key={p.id}
                className="flex items-start gap-2 px-3 py-2 hover:bg-[#f4f6f1]"
                data-testid={`${testid}-row-${p.id}`}
              >
                <button
                  type="button"
                  onClick={() => { onApply(p); setOpen(false); toast.success(`Loaded "${p.name}"`); }}
                  className="flex-1 text-left min-w-0"
                  data-testid={`${testid}-apply-${p.id}`}
                >
                  <div className="font-semibold text-[13px] text-[#0B1E3A] truncate inline-flex items-center gap-1">
                    <Check className="w-3 h-3 text-[#0A3D79] opacity-70" />
                    {p.name}
                  </div>
                  {p.provider_number && <div className="text-[11.5px] text-[#3A5275] truncate">Provider #{p.provider_number}</div>}
                  {p.npi && <div className="text-[11.5px] text-[#3A5275] truncate">NPI {p.npi}</div>}
                </button>
                <button
                  type="button"
                  onClick={() => { onDelete?.(p.id); toast("Profile removed."); }}
                  className="text-[#c05746] p-1.5 rounded hover:bg-[#fbeae5]"
                  data-testid={`${testid}-delete-${p.id}`}
                  aria-label="Delete profile"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onSaveClick}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#0A3D79] text-white text-[12.5px] font-semibold hover:bg-[#2b5545]"
        data-testid={`${testid}-save`}
        title="Save current values for reuse on the next client"
      >
        <BookmarkPlus className="w-3.5 h-3.5" /> Save current
      </button>
    </div>
  );
}
